import { type LossClassification } from '@/types/jobs';
import { type JobCreateFormState } from '@/pages/jobs/hooks/useJobCreateFormState';
import { deriveLossRiskLevel } from '@/utils/lossRisk';

export const useJobClassificationEdit = (form: JobCreateFormState) => {
    const {
        iicrcCategory,
        iicrcClass,
        setIicrcCategory,
        setIicrcClass,
        setClassificationManuallyEdited,
        setClassificationReasoningChanges,
        pendingClassificationChange,
        setPendingClassificationChange,
    } = form;

    const requestClassificationChange = (field: 'category' | 'class', value: number) => {
        const current = field === 'category' ? iicrcCategory : iicrcClass;
        if (value === current) return;
        setPendingClassificationChange({ field, value });
    };

    const confirmClassificationChange = () => {
        if (!pendingClassificationChange) return;
        if (pendingClassificationChange.field === 'category') {
            setIicrcCategory(pendingClassificationChange.value);
        } else {
            setIicrcClass(pendingClassificationChange.value);
        }
        setClassificationManuallyEdited(true);
        setClassificationReasoningChanges((prev) => ({
            ...prev,
            [pendingClassificationChange.field]: true,
        }));
        setPendingClassificationChange(null);
    };

    const buildClassification = (): LossClassification | null => {
        if (iicrcCategory === 0 && iicrcClass === 0) return null;
        const catDesc: Record<number, string> = { 1: 'Clean Water', 2: 'Gray Water', 3: 'Black Water' };
        const clsDesc: Record<number, string> = { 1: 'Minor / Partial Room', 2: 'Significant / Full Room', 3: 'Extensive / Saturated', 4: 'Specialty Drying' };
        return {
            category: iicrcCategory,
            categoryDescription: catDesc[iicrcCategory] || 'Unknown',
            class: iicrcClass,
            classDescription: clsDesc[iicrcClass] || 'Unknown',
            riskLevel: deriveLossRiskLevel(iicrcCategory, iicrcClass),
        };
    };

    return {
        requestClassificationChange,
        confirmClassificationChange,
        buildClassification,
    };
};
